import { Component, Input, OnInit } from '@angular/core';
import { SegmentOneTimeFilters } from '@models/rate-card/segment-one-time-filters';
import { CommonSegmentFilters } from '@models/rate-card/segment-common-filters';
import { AuthService } from '@services/auth/auth.service';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { NewDynamicSegments, NewSegmentData, NewSegmentMultiStationData } from '@models/rate-card/rate-card';
import { validateFilters } from '@shared/helpers/functions/filter-helpers';
import moment from 'moment';
import { NotificationService } from '@services/notification/notification.service';
import { SegmentBaseComponent } from '../segment-base.component';
import { RateCardService } from '@services/rate-card/rate-card.service';
import { Segmentation } from '@models/config';
import { cloneDeep } from 'lodash';
import { TimeFilter } from '@models/filter-types';

@Component({
    selector: 'app-one-time-segment',
    templateUrl: './one-time-segment.component.html',
    styleUrls: ['./one-time-segment.component.scss'],
    standalone: false,
})
export class OneTimeSegmentComponent extends SegmentBaseComponent implements OnInit {
    @Input() canAddRow = true;
    @Input() hideRate = false;
    isBulkAddSegment = false;

    oneTimeFilters!: SegmentOneTimeFilters[];
    commonSegmentFilters!: CommonSegmentFilters;

    constructor(
        protected authSvc: AuthService,
        protected lookupSvc: LookupV2Service,
        protected snackBarSvc: NotificationService,
        protected rateCardSvc: RateCardService,
    ) {
        super(authSvc, lookupSvc, snackBarSvc, rateCardSvc);
        this.isBulkAddSegment = false;
        // this.isBulkAddSegment = this.config.roleCapability.rate_optics.dynamic_rate_card.bulk_add_segment;
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.oneTimeFilters = [
            new SegmentOneTimeFilters(
                this.lookupSvc,
                this.config,
                this.rowData.rateCardTab,
                this.rowData.channelGroupId,
                this.rowData.channelId,
                this.rowData.spotTypeId,
                this.rowData.daypartId,
                this.rowData.priceFloor,
                this.rowData.priceMax,
                this.rowData.primarySpotLength,
                this.rowData.primaryTier,
                this.segmentData,
                this.hideRate,
            ),
        ];
        const isSegmentData = this.segmentData ? true : false;
        if (this.isBulkAddSegment && !isSegmentData) {
            this.commonSegmentFilters = new CommonSegmentFilters(
                this.lookupSvc,
                this.config,
                this.rowData.rateCardTab,
                this.rowData.channelGroupId,
                this.rowData.marketId,
                this.rowData.stationId,
                this.rowData.channelId,
                this.segmentData,
            );
        }
    }

    addRowOneTime(): void {
        if (this.canAddRow) {
            const existingFilter = this.oneTimeFilters[this.oneTimeFilters.length - 1];
            const newFilter = new SegmentOneTimeFilters(
                this.lookupSvc,
                this.config,
                this.rowData.rateCardTab,
                this.rowData.channelGroupId,
                this.rowData.channelId,
                this.rowData.spotTypeId,
                this.rowData.daypartId,
                this.rowData.priceFloor,
                this.rowData.priceMax,
                this.rowData.primarySpotLength,
                this.rowData.primaryTier,
                this.segmentData,
                this.hideRate,
            );
            newFilter.oneTimeDate.setValueNoOnChange(cloneDeep(existingFilter.oneTimeDate.Value));
            newFilter.rateFilter.setValueNoOnChange(cloneDeep(existingFilter.rateFilter.Value));
            if (existingFilter.dynamicFilters.startTimeFilter) {
                (newFilter.dynamicFilters.startTimeFilter as TimeFilter).setValueNoOnChange(
                    cloneDeep(existingFilter.dynamicFilters.startTimeFilter.Value as moment.Moment),
                );
            }
            if (existingFilter.dynamicFilters.endTimeFilter) {
                (newFilter.dynamicFilters.endTimeFilter as TimeFilter).setValueNoOnChange(
                    cloneDeep(existingFilter.dynamicFilters.endTimeFilter.Value as moment.Moment),
                );
            }
            this.oneTimeFilters.push(newFilter);
        }
    }

    save() {
        const newRowData: NewSegmentData[] = [];
        if (validateFilters(this.oneTimeFilters).isValid) {
            if (this.isBulkAddSegment && this.commonSegmentFilters && this.commonSegmentFilters.stations.Value.length > 1) {
                this.saveAndCloseMultiAddSegmentPopup();
                return;
            }

            for (const filter of this.oneTimeFilters) {
                const dynamicSegments: NewDynamicSegments[] = this.generateDynamicSegmentValues(filter);

                const filterRowData: NewSegmentData = {
                    daypartId: filter.dayparts.Value.id,
                    rate: filter.rateFilter.Value,
                    isOneTimeSegment: true,
                    dynamicSegments,
                    ...(this.segmentData?.requestId ? { requestId: this.segmentData.requestId } : {}),
                };

                filterRowData.oneTimeSegment = {
                    date: moment(filter.oneTimeDate.Value).format('YYYY-MM-DD'),
                };
                newRowData.push(filterRowData);
            }
            this.saveAndCloseAddSegmentPopup(newRowData);
        } else {
            this.snackBarSvc.openSnackBar('At least one filter is invalid', 'error');
        }
    }

    generateNewDowDynamicSegment(segment: Segmentation, filter: SegmentOneTimeFilters) {
        let segmentValue: string;
        this.lookupSvc.getSegmentDOW().subscribe((dowsFilter) => {
            const dow = moment(filter.oneTimeDate.Value).format('ddd');
            segmentValue = dowsFilter.find((day) => (day.name = dow)).name;
        });
        const dynamicSegment: NewDynamicSegments = {
            segmentType: segment.segment_type,
            segmentValue,
        };
        return dynamicSegment;
    }

    saveAndCloseMultiAddSegmentPopup() {
        const newRowData: NewSegmentMultiStationData[] = [];

        const channelSelected =
            this.commonSegmentFilters.channels.Value.length > 0
                ? this.commonSegmentFilters.channels.Value.map((channel) => channel.id)
                : [this.rowData.channelId];

        const parentSegmentData = {
            daypartId: this.segmentData ? this.segmentData.parentSegmentData.daypartId : this.rowData.daypartId,
            dynamicSegmentation: this.segmentData
                ? this.segmentData.parentSegmentData.dynamicSegmentation
                : this.rowData.parentDynamicSegmentation,
        };

        for (const filter of this.oneTimeFilters) {
            const dynamicSegments: NewDynamicSegments[] = this.generateDynamicSegmentValues(filter);

            const filterRowData: NewSegmentMultiStationData = {
                daypartId: filter.dayparts.Value.id,
                isOneTimeSegment: true,
                dynamicSegments,
                ...(this.segmentData?.requestId ? { requestId: this.segmentData.requestId } : {}),
                startDate: moment(filter.oneTimeDate.Value).format('YYYY-MM-DD'),
                endDate: null,
                hiatusWeeks: [],
                dows: [],
                channels: channelSelected,
                rateCardTab: this.rowData.rateCardTab,
                isDynamic: this.rowData.isDynamic,
                isPolitical: this.rowData.isPolitical,
                spotTypeId: this.rowData.spotTypeId,
                rate: filter.rateFilter.Value,
                reloadParams: this.rowData.reloadParams,
                parentSegmentData,
            };
            newRowData.push(filterRowData);
        }
        this.commonSaveFunc(newRowData);
    }
}
